import { Snackbar, styled } from '@mui/material';
import React, { useState } from 'react';
import { Options } from '../Options';
import { IBusinessJobDetailsModel } from '../../BusinessJob/models';
import CancelModal from './CancelModal';
import { startOfTomorrow, isAfter, isSameDay, startOfToday, addMinutes } from 'date-fns';
import EditModal from './EditModal';
import { Download, Edit, EventBusy, ReadMore, Star, Close, Visibility, PersonAdd, Message } from '@mui/icons-material';
import { useUserContext } from 'UserContext';
import RevealModal from './RevealModal';
import { client } from 'shared';
import { BusinessSlot, ShiftJob, ShiftProviderUser } from 'models';
import UpdatePreferredWorkerModal from './UpdatePreferredWorkersModal';
import { IHourReport } from 'internal/payments/HourReportModels';
import { getTimestampByType } from '../../../../subs/HourReportMethods';
import DocumentDownloadInfo from './DocumentDownloadInfo';
import { getDocumentsFolderForUserId } from 'parent-portal/provider-profile/tabs/documents/DocumentShared';
import { IconButton } from 'library/ui';

export default function ShiftCardMenu({
    provider_id,
    slot,
    ongoing_request,
    provider,
    onClose,
    status,
    refresh,
    hourReport,
    needs718bFiling,
    businessLocationId,
}: {
    provider_id?: number;
    slot: BusinessSlot;
    ongoing_request: IBusinessJobDetailsModel | ShiftJob;
    provider?: ShiftProviderUser;
    onClose?: () => void;
    status: string;
    refresh: () => void;
    hourReport?: IHourReport;
    needs718bFiling?: boolean;
    businessLocationId?: number;
}) {
    const [snackOpen, setSnackOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [ssnInputOpen, setSsnInputOpen] = useState(false);
    const [updatePreferredOpen, setUpdatePreferredOpen] = useState(false);
    const [showDocDownloadInfo, setShowDocDownloadInfo] = useState(false);

    const { user } = useUserContext();

    const shift = slot.appointments?.length ? slot.appointments[0] : undefined;
    const isOpen = status === 'UNFILLED';
    const isFilled = status === 'ON_STAFF';
    const isFutureShift = isAfter(new Date(slot.start_date), startOfTomorrow());
    const isTodayOrLater = isAfter(new Date(slot.start_date), startOfToday());
    const isToday = isSameDay(new Date(slot.start_date), new Date());
    const startDateMinusOneHour = addMinutes(new Date(slot.start_date), -60);
    const endDatePlusOneHour = addMinutes(new Date(slot.end_date), 60);
    const now = new Date();
    const canMessage = now > startDateMinusOneHour && now < endDatePlusOneHour;

    const clockIn = getTimestampByType(hourReport, 'provider_clock_in');

    const handleClose = () => {
        setSnackOpen(false);
    };

    const showModal = () => {
        if (user?.address?.[0]?.state === 'Illinois') setShowDocDownloadInfo(!!needs718bFiling);
    };

    const setLoadingFalse = () => {
        setSnackOpen(false);
    };

    function showSSNReveal() {
        let showSSN = false;
        user?.address?.forEach((address) => {
            if (address.state === 'Florida') {
                showSSN = true;
            }
        });
        return showSSN;
    }

    const onSendCode = async () => {
        try {
            setSsnInputOpen(true);
            client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider?.id } });
        } catch (error) {
            alert('Cannot send SSN reveal code');
        }
    };

    const documentOption = {
        text: 'Download documents',
        onClick: () => {
            const userId = provider?.id;
            if (userId) {
                getDocumentsFolderForUserId(userId, showModal, setLoadingFalse, businessLocationId, undefined);
            }
        },
        icon: <Download />,
    };
    const detailOption = {
        text: 'Job details',
        to: `/jobs/${ongoing_request.id}`,
        icon: <ReadMore />,
    };

    const editOption = {
        text: 'Edit times',
        onClick: () => setEditOpen(true),
        icon: <Edit />,
    };
    const cancelOption = {
        text: 'Cancel this shift',
        onClick: () => setCancelOpen(true),
        icon: <EventBusy />,
    };
    const updatePreferredWorkerOption = {
        text: 'Request previous worker',
        onClick: () => setUpdatePreferredOpen(true),
        icon: <PersonAdd />,
    };
    const reviewOption = { text: 'Provide feedback', to: `/providers/review/${provider?.id}`, icon: <Star /> };
    const ssnOption = {
        text: 'Reveal SSN',
        onClick: () => onSendCode(),
        icon: <Visibility />,
    };
    const messageOption = { text: 'Message', to: `/messages/${provider?.id}`, icon: <Message /> };

    const menuOptions = [
        { option: documentOption, show: !!provider_id },
        { option: detailOption, show: !!ongoing_request.id },
        { option: editOption, show: shift && (isOpen || isFilled) && isTodayOrLater && !clockIn },
        {
            option: cancelOption,
            show: ((isOpen || isFilled) && isFutureShift) || (isToday && !shift?.hour_reports.length),
        },
        { option: updatePreferredWorkerOption, show: shift && isOpen && isFutureShift },
        { option: reviewOption, show: !!provider && status === 'COMPLETED' },
        { option: ssnOption, show: !!provider_id && showSSNReveal() },
        { option: messageOption, show: !!provider_id && canMessage },
    ];

    return (
        <>
            <MenuTrigger>
                <Options
                    options={menuOptions.filter((option) => option.show).map((option) => option.option)}
                    onClose={onClose}
                />
            </MenuTrigger>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={snackOpen}
                onClose={handleClose}
                message="Downloading documents..."
                action={<IconButton icon={Close} size={26} iconSize={16} onClick={handleClose} />}
            />
            <CancelModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                slot={slot}
                providerName={provider?.first_name}
                providerId={provider_id}
                refresh={refresh}
            />
            <DocumentDownloadInfo
                showDocDownloadInfo={showDocDownloadInfo}
                setShowDocDownloadInfo={setShowDocDownloadInfo}
            />

            {shift && (
                <>
                    <EditModal
                        open={editOpen}
                        onClose={() => setEditOpen(false)}
                        shift={shift}
                        providerName={provider?.first_name}
                        refresh={refresh}
                        timezone={shift.ongoing_request.timezone}
                    />
                    <UpdatePreferredWorkerModal
                        open={updatePreferredOpen}
                        onClose={() => setUpdatePreferredOpen(false)}
                        shift={shift}
                        refresh={refresh}
                    />
                    {provider ? (
                        <RevealModal open={ssnInputOpen} onClose={() => setSsnInputOpen(false)} provider={provider} />
                    ) : null}
                </>
            )}
        </>
    );
}

const MenuTrigger = styled('div')({
    display: 'flex',
    position: 'relative',
    top: 0,
    right: 0,
});
